/**
 * @file businessRulesEngine.ts
 * Core business rules validation engine with localization support
 */

import type {
  BusinessRule,
  BusinessRuleCondition,
  BusinessRuleExpression,
  BusinessRuleError,
  BusinessRuleValidationResult,
  LocalizedValidationContext,
  BusinessRuleOperator,
} from 'types/businessRules';

// Type guard functions (moved here from types to avoid runtime imports)
function isBusinessRuleExpression(
  expr: BusinessRuleExpression | BusinessRuleCondition
): expr is BusinessRuleExpression {
  return 'property' in expr && 'operator' in expr;
}

function isBusinessRuleCondition(
  expr: BusinessRuleExpression | BusinessRuleCondition
): expr is BusinessRuleCondition {
  return 'operator' in expr && 'expressions' in expr && !('property' in expr);
}

export class LocalizedBusinessRulesEngine {

  /**
   * Main validation entry point
   */
  async validateBusinessRules(
    context: LocalizedValidationContext,
    schema: any
  ): Promise<BusinessRuleValidationResult> {

    const applicableRules = this.getApplicableRules(context, schema);
    const results: BusinessRuleError[] = [];
    const warnings: BusinessRuleError[] = [];

    context.fastify?.log.debug(
      `[BusinessRules] Evaluating ${applicableRules.length} applicable rules for ${context.operation}`
    );

    for (const rule of applicableRules) {
      // First check if the rule should be applied
      if (rule.applyWhen && !this.evaluateCondition(rule.applyWhen, context).valid) {
        context.fastify?.log.debug(
          `[BusinessRules] Skipping rule "${rule.name}" - applyWhen condition not met`
        );
        continue;
      }

      context.fastify?.log.debug(`[BusinessRules] Evaluating rule "${rule.name}"`);

      // Evaluate the actual rule condition
      const ruleResult = this.evaluateCondition(rule.condition, context, rule);

      if (ruleResult.valid === false) {
        const localizedError: BusinessRuleError = {
          ruleId: rule.id,
          ruleName: this.localizeRuleName(rule, context),
          message: this.localizeMessage(rule.messageKey, ruleResult.messageParams || {}, context),
          rawMessageKey: rule.messageKey,
          messageParams: ruleResult.messageParams,
          severity: rule.severity,
          blockingBehavior: rule.blockingBehavior || 'block'
        };

        if (rule.severity === 'error') {
          results.push(localizedError);
        } else {
          warnings.push(localizedError);
        }

        context.fastify?.log.warn(
          `[BusinessRules] Rule "${rule.name}" failed: ${localizedError.message}`
        );
      } else {
        context.fastify?.log.debug(`[BusinessRules] Rule "${rule.name}" passed`);
      }
    }

    return {
      valid: results.length === 0,
      errors: results,
      warnings
    };
  }

  /**
   * Get rules that apply to the current context
   */
  private getApplicableRules(context: LocalizedValidationContext, schema: any): BusinessRule[] {
    const rules: BusinessRule[] = [];

    // Sphere-level rules
    if (context.type === 'sphere' && schema.businessRules) {
      rules.push(...schema.businessRules.filter((rule: BusinessRule) =>
        rule.enabled && rule.executeOn.includes(context.operation)
      ));
    }

    // Flow-level rules
    if (context.type === 'flow' && schema.flow?.businessRules) {
      rules.push(...schema.flow.businessRules.filter((rule: BusinessRule) => {
        if (!rule.enabled) return false;
        if (!rule.executeOn.includes(context.operation)) return false;

        if (rule.flowStage && context.flowContext) {
          return rule.flowStage === context.flowContext.currentStage;
        }
        return true;
      }));
    }

    return rules;
  }

  /**
   * Evaluate a condition (which may contain nested conditions)
   */
  private evaluateCondition(
    condition: BusinessRuleCondition,
    context: LocalizedValidationContext,
    parentRule?: BusinessRule
  ): { valid: boolean; messageParams?: Record<string, any> } {

    const results: boolean[] = [];
    const failedExpressions: string[] = [];
    const messageParams: Record<string, any> = {};

    for (const expr of condition.expressions) {
      if (isBusinessRuleExpression(expr)) {
        const result = this.evaluateExpression(expr, context);
        results.push(result.valid);

        if (!result.valid) {
          const localizedMessage = this.localizeExpressionMessage(expr, result, context);
          failedExpressions.push(localizedMessage);

          // Merge message parameters
          Object.assign(messageParams, result.messageParams || {});
        }
      } else if (isBusinessRuleCondition(expr)) {
        const nestedResult = this.evaluateCondition(expr, context, parentRule);
        results.push(nestedResult.valid);

        if (!nestedResult.valid) {
          Object.assign(messageParams, nestedResult.messageParams || {});
        }
      }
    }

    const isValid = condition.operator === 'and'
      ? results.every(r => r === true)
      : results.includes(true);

    if (!isValid) {
      messageParams['failedExpressions'] = failedExpressions;
      messageParams['conditionOperator'] = context.t(`business_rules.operators.${condition.operator}`);
    }

    return { valid: isValid, messageParams };
  }

  /**
   * Evaluate a single expression
   */
  private evaluateExpression(
    expression: BusinessRuleExpression,
    context: LocalizedValidationContext
  ): { valid: boolean; messageParams?: Record<string, any> } {

    const value = this.getNestedProperty(context.data, expression.property, context);
    let compareValue = expression.value;

    if (expression.compareToProperty) {
      compareValue = this.getNestedProperty(context.data, expression.compareToProperty, context);
    }


    const result = this.performComparison(expression.operator, value, compareValue);

    // Build message parameters for localization
    const messageParams: Record<string, any> = {
      propertyName: expression.property,
      propertyDisplayName: this.getLocalizedPropertyName(expression.property, context),
      actualValue: this.formatValue(value, context),
      expectedValue: this.formatValue(compareValue, context),
      operator: context.t(`business_rules.operators.${expression.operator}`),
      ...expression.messageParams
    };

    // Add specific values for cross-property comparisons
    if (expression.compareToProperty) {
      messageParams['comparePropertyName'] = expression.compareToProperty;
      messageParams['comparePropertyDisplayName'] = this.getLocalizedPropertyName(expression.compareToProperty, context);
      messageParams[`${expression.property}Value`] = this.formatValue(value, context);
      messageParams[`${expression.compareToProperty}Value`] = this.formatValue(compareValue, context);
    }

    context.fastify?.log.debug(
      `[BusinessRules] Expression ${expression.property} ${expression.operator} ${compareValue}: ${result}`,
      {
        property: expression.property,
        value,
        valueType: typeof value,
        compareValue,
        compareValueType: typeof compareValue,
        operator: expression.operator,
        result,
        allData: JSON.stringify(context.data),
        compareToProperty: expression.compareToProperty,
        isComparable: this.isComparable(value, compareValue),
        isDateString1: this.isDateString(value),
        isDateString2: this.isDateString(compareValue),
        comparisonResult: this.isComparable(value, compareValue) ? this.compareValues(value, compareValue) : 'not-comparable'
      }
    );

    return { valid: result, messageParams };
  }

  /**
   * Perform the actual comparison based on operator
   */
  private performComparison(operator: BusinessRuleOperator, value: any, compareValue: any): boolean {
    switch (operator) {
      case 'equals':
        return value === compareValue;
      case 'not_equals':
        return value !== compareValue;
      case 'greater_than':
        return this.isComparable(value, compareValue) && this.compareValues(value, compareValue) > 0;
      case 'less_than':
        return this.isComparable(value, compareValue) && this.compareValues(value, compareValue) < 0;
      case 'greater_than_or_equal':
        return this.isComparable(value, compareValue) && this.compareValues(value, compareValue) >= 0;
      case 'less_than_or_equal':
        return this.isComparable(value, compareValue) && this.compareValues(value, compareValue) <= 0;
      case 'contains':
        return typeof value === 'string' && typeof compareValue === 'string' && value.includes(compareValue);
      case 'exists':
        return value !== undefined && value !== null && value !== '';
      case 'is_true':
        return value === true;
      case 'is_false':
        return value === false;
      case 'in':
        return Array.isArray(compareValue) && compareValue.includes(value);
      case 'not_in':
        return Array.isArray(compareValue) && !compareValue.includes(value);
      default:
        return false;
    }
  }

  /**
   * Get nested property value with support for array indexing
   */
  private getNestedProperty(obj: any, path: string, context?: LocalizedValidationContext): any {
    try {
      const result = path.split('.').reduce((current, key) => {
        // Handle array access like "keyDates[0].date"
        if (key.includes('[') && key.includes(']')) {
          const [arrayProp, indexStr] = key.split('[');
          const index = Number.parseInt(indexStr.replace(/]/g, ''), 10);
          return current && Array.isArray(current[arrayProp]) ? current[arrayProp][index] : undefined;
        }

        // Handle special case for array length
        if (key === 'length' && Array.isArray(current)) {
          return current.length;
        }

        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);

      return result;
    } catch (error) {
      // Log the error for debugging but return undefined as fallback
      context?.fastify?.log.debug(`Error accessing property path '${path}':`, error);
      return undefined;
    }
  }

  /**
   * Localize a message using the translation function
   */
  private localizeMessage(
    messageKey: string,
    params: Record<string, any>,
    context: LocalizedValidationContext
  ): string {
    try {
      return context.t(messageKey, params);
    } catch (error) {
      // Fallback to key if translation fails, log the error for debugging
      context.fastify?.log.warn(`Translation failed for key '${messageKey}':`, error);
      return `${messageKey} (${JSON.stringify(params)})`;
    }
  }

  /**
   * Get localized rule name
   */
  private localizeRuleName(
    rule: BusinessRule,
    context: LocalizedValidationContext
  ): string {
    if (rule.nameKey) {
      return this.localizeMessage(rule.nameKey, {}, context);
    }
    return rule.name; // Fallback to original name
  }

  /**
   * Get localized message for a specific expression
   */
  private localizeExpressionMessage(
    expression: BusinessRuleExpression,
    result: { messageParams?: Record<string, any> },
    context: LocalizedValidationContext
  ): string {

    const messageKey = expression.messageKey || this.getDefaultMessageKey(expression.operator);
    const params = result.messageParams || {};

    return this.localizeMessage(messageKey, params, context);
  }

  /**
   * Get localized property name from schema or fallback to humanized version
   */
  private getLocalizedPropertyName(
    propertyPath: string,
    context: LocalizedValidationContext
  ): string {

    // Try to get localized property name from schema
    if (context.schemaLocalizations) {
      const propertyKey = `schema.properties.${propertyPath.split('.')[0]}.label`;
      try {
        const localized = context.t(propertyKey);
        if (localized !== propertyKey) {
          return localized;
        }
      } catch {
        // Fall through to default
      }
    }

    // Fallback: humanize the property name
    return propertyPath
      .split('.')
      .pop()
      ?.replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str: string) => str.toUpperCase()) || propertyPath;
  }

  /**
   * Format a value for display in error messages
   */
  private formatValue(value: any, context: LocalizedValidationContext): string {
    if (value === null || value === undefined) {
      return context.t('business_rules.common.empty_value', { defaultValue: 'N/A' });
    }

    if (value instanceof Date) {
      return value.toLocaleDateString(context.locale);
    }

    if (typeof value === 'number') {
      return value.toLocaleString(context.locale);
    }

    return String(value);
  }

  /**
   * Get default message key for an operator
   */
  private getDefaultMessageKey(operator: BusinessRuleOperator): string {
    const operatorMessageMap: Record<BusinessRuleOperator, string> = {
      'equals': 'business_rules.common.field_must_equal',
      'not_equals': 'business_rules.common.field_must_not_equal',
      'greater_than': 'business_rules.common.field_must_be_greater',
      'less_than': 'business_rules.common.field_must_be_less',
      'greater_than_or_equal': 'business_rules.common.field_must_be_greater_or_equal',
      'less_than_or_equal': 'business_rules.common.field_must_be_less_or_equal',
      'contains': 'business_rules.common.field_must_contain',
      'exists': 'business_rules.common.field_must_exist',
      'is_true': 'business_rules.common.field_must_be_true',
      'is_false': 'business_rules.common.field_must_be_false',
      'in': 'business_rules.common.field_must_be_one_of',
      'not_in': 'business_rules.common.field_must_not_be_one_of'
    };

    return operatorMessageMap[operator] || 'business_rules.common.validation_failed';
  }

  /**
   * Check if value is numeric
   */
  private isNumeric(value: any): boolean {
    return typeof value === 'number' && !Number.isNaN(value);
  }

  /**
   * Check if two values can be compared (both numbers or both dates)
   */
  private isComparable(value1: any, value2: any): boolean {
    // Both are numbers
    if (this.isNumeric(value1) && this.isNumeric(value2)) {
      return true;
    }

    // Both are Date objects
    if (value1 instanceof Date && value2 instanceof Date) {
      return true;
    }

    // Both are date strings (ISO format)
    if (this.isDateString(value1) && this.isDateString(value2)) {
      return true;
    }

    return false;
  }

  /**
   * Check if a value is a valid date string
   */
  private isDateString(value: any): boolean {
    if (typeof value !== 'string') return false;

    // Check for ISO date format and validate it can be parsed
    const date = new Date(value);
    return !Number.isNaN(date.getTime());
  }

  /**
   * Compare two values, returning -1, 0, or 1
   * Handles both numbers and dates
   */
  private compareValues(value1: any, value2: any): number {
    // Handle numbers
    if (this.isNumeric(value1) && this.isNumeric(value2)) {
      return value1 - value2;
    }

    // Handle Date objects
    if (value1 instanceof Date && value2 instanceof Date) {
      return value1.getTime() - value2.getTime();
    }

    // Handle date strings
    if (this.isDateString(value1) && this.isDateString(value2)) {
      const date1 = new Date(value1);
      const date2 = new Date(value2);
      return date1.getTime() - date2.getTime();
    }

    // Fallback to string comparison
    return String(value1).localeCompare(String(value2));
  }
}